import { forwardRef, type CSSProperties, type ReactNode } from 'react';
import { foundryLogoUrl } from '../asset-urls';
import { cn } from '../lib/cn';

export interface FoundryShellHeaderProps {
  chrome: 'electron' | 'web';
  title?: ReactNode;
  leading?: ReactNode;
  center?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
  testId?: string;
  className?: string;
  leadingClassName?: string;
  centerClassName?: string;
  actionsClassName?: string;
  showBrandMark?: boolean;
  /** Small uppercase tag rendered beside the brand mark (e.g. LOCAL / CLOUD). */
  brandTag?: ReactNode;
  style?: CSSProperties;
}

export const FoundryShellHeader = forwardRef<HTMLElement, FoundryShellHeaderProps>(
  function FoundryShellHeader(
    {
      chrome,
      title,
      leading,
      center,
      actions,
      children,
      testId,
      className,
      leadingClassName,
      centerClassName,
      actionsClassName,
      showBrandMark = false,
      brandTag,
      style,
    },
    ref,
  ): React.JSX.Element {
    const electron = chrome === 'electron';
    const centerContent =
      center ?? (showBrandMark ? <FoundryShellBrandMark tag={brandTag} /> : null);

    return (
      <header
        ref={ref}
        data-testid={testId}
        data-shell-chrome={chrome}
        className={cn(
          'relative grid h-16 shrink-0 grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-3 border-b border-white/10',
          electron ? 'app-region-drag pl-24 pr-6' : 'px-6',
          className,
        )}
        style={style}
      >
        {children ?? (
          <>
            <div
              className={cn('flex min-w-0 items-center gap-2.5 overflow-hidden', leadingClassName)}
            >
              {leading ?? <p className="truncate text-sm font-semibold text-white/70">{title}</p>}
            </div>
            {/* Empty placeholder keeps the 3-column grid intact; the center
                content is overlaid absolutely so it's centered to the WINDOW,
                not to the padded grid box (the asymmetric `pl-24` traffic-light
                inset would otherwise shift it ~36px right of true centre). */}
            <div aria-hidden className="min-w-0" />
            <div
              className={cn(
                'flex min-w-0 items-center justify-end gap-2',
                electron && 'app-region-no-drag',
                actionsClassName,
              )}
            >
              {actions}
            </div>
            {centerContent && (
              <div className="pointer-events-none absolute inset-y-0 left-1/2 flex -translate-x-1/2 items-center">
                <div className={cn('pointer-events-auto flex items-center', centerClassName)}>
                  {centerContent}
                </div>
              </div>
            )}
          </>
        )}
      </header>
    );
  },
);

function FoundryShellBrandMark({ tag }: { tag?: ReactNode }): React.JSX.Element {
  return (
    <span className="relative inline-flex">
      <img src={foundryLogoUrl} alt="Foundry" className="h-7 w-auto max-w-full" draggable={false} />
      {tag != null && tag !== '' && (
        // Anchored to the RIGHT edge of the logo and near its BOTTOM line,
        // overlapping the logo a little (left-full pulled back by -translate-x-2).
        // Absolute, so it adds no width — the logo image stays put and centered.
        <span className="pointer-events-none absolute -bottom-0.5 left-full flex -translate-x-2">
          <span
            className="rounded-full bg-black/70 px-1 py-px text-[8px] font-semibold uppercase leading-none tracking-wide text-white/85"
            data-testid="foundry-brand-tag"
          >
            {tag}
          </span>
        </span>
      )}
    </span>
  );
}
